import { useParams } from "react-router-dom";
import { useReport } from "../api/hooks.js";
import { ErrorState, StatePanel } from "../components/primitives.jsx";
import { Widget, WidgetSkeleton } from "../components/Widget.jsx";
import { useFilters } from "../lib/useFilters.js";
import { filterSlug } from "../../shared/filterSlug.js";
import { DataStrip, PageHeader } from "../components/PageHeader.jsx";
import { ScopeBanner } from "../components/Scope.jsx";
import { fmtDate } from "../lib/format.js";
import { findDashboard, findTab } from "../../shared/catalog.js";
import { tabTitle, useTenant } from "../tenant/TenantProvider.jsx";

export default function ReportPage() {
  const { dashboard, tab } = useParams();
  const f = useFilters();
  const { data, error, isLoading, isFetching, refetch } = useReport(dashboard, tab, f.apiQuery);
  const dash = findDashboard(dashboard);
  const tabMeta = findTab(dashboard, tab);
  const { current } = useTenant();

  if (error?.kind === "forbidden") return <StatePanel title="No access" detail={error.message || "You do not have permission to view this page."} />;

  const basis = data ? `${fmtDate(data.window.prevFrom)} – ${fmtDate(data.window.prevTo)}` : "the previous period";

  return (
    <div className="flex flex-col gap-4">
      <DataStrip data={data} isFetching={isFetching} today={f.today} />
      {/* A page can phrase its title as the question it answers (mockup), else the tab title. */}
      <PageHeader title={tabMeta?.heading ?? tabTitle(tabMeta, current)} subtitle={tabMeta?.subtitle ?? `${dash?.title} · ${dash?.description}`} />

      {data && <ScopeBanner key={data.report} page={data} onRemove={(keys) => f.update(Object.fromEntries(keys.map((k) => [k, ""])))} />}

      {error && <ErrorState what="this report" message={error.message} retryable={error.retryable !== false} onRetry={() => refetch()} />}

      <div className={`grid grid-cols-12 gap-4 transition-opacity ${isFetching && data ? "opacity-60" : ""}`}>
        {isLoading && [12, 8, 4, 6, 6].map((s, i) => <WidgetSkeleton key={i} span={s} />)}
        {data?.widgets.map((w) => (
          <Widget
            key={`${data.report}-${w.id}`}
            widget={w}
            page={data}
            basis={basis}
            filenameBase={`${f.filters.tenant}_${dashboard}_${tab}_${f.filters.from}_${f.filters.to}${filterSlug(data.filters)}`}
            onRetry={refetch}
          />
        ))}
      </div>
    </div>
  );
}
